import * as React from 'react'
import clsx from 'clsx'
import { isFragment } from 'react-is'
import { TabScrollButton } from '../TabScrollButton'
import { useSlotProps } from '../utils/useSlotProps'
import { debounce } from '../../utils/debouce'
import animate from '../internal/animate'
import ScrollbarSize from './ScrollbarSize'
import {
  getNormalizedScrollLeft,
  detectScrollType,
} from '../../utils/scrollLeft'
import { useEventCallback } from '../../hooks/useEventCallback'
import { ownerDocument, ownerWindow } from '../../utils'
import type { TabsProps } from './Types'
import style from './index.module.scss'

const transitionDurationStandard = 300

const nextItem = (list: Element, item: Element | null): ChildNode | null => {
  if (list === item) {
    return list.firstChild
  }
  if (item && item.nextElementSibling) {
    return item.nextElementSibling
  }
  return list.firstChild
}

const previousItem = (
  list: Element,
  item: Element | null
): ChildNode | null => {
  if (list === item) {
    return list.lastChild
  }
  if (item && item.previousElementSibling) {
    return item.previousElementSibling
  }
  return list.lastChild
}

const moveFocus = (
  list: Element,
  currentFocus: Element | null,
  traversalFunction: (list: Element, item: Element | null) => ChildNode | null
) => {
  let wrappedOnce = false
  let nextFocus = traversalFunction(list, currentFocus)

  while (nextFocus) {
    // Prevent infinite loop.
    if (nextFocus === list.firstChild) {
      if (wrappedOnce) {
        return
      }
      wrappedOnce = true
    }

    const nextFocusDisabled =
      // @ts-ignore
      nextFocus.disabled || nextFocus.getAttribute('aria-disabled') === 'true'
    //@ts-ignore
    if (!nextFocus.hasAttribute('tabindex') || nextFocusDisabled) {
      // Move to the next element.
      // @ts-ignore
      nextFocus = traversalFunction(list, nextFocus)
    } else {
      // @ts-ignore
      nextFocus.focus()
      return
    }
  }
}

const defaultIndicatorStyle = {}

let warnedOnceTabPresent = false

export const Tabs = React.forwardRef<HTMLButtonElement, TabsProps>(
  function Tabs(props, ref) {
    const isRtl = false
    const direction = 'ltr'
    const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      action,
      centered = false,
      children: childrenProp,
      className,
      Component = 'div',
      allowScrollButtonsMobile = false,
      indicatorColor = 'primary',
      onChange,
      orientation = 'horizontal',
      ScrollButtonComponent = TabScrollButton,
      scrollButtons = 'auto',
      selectionFollowsFocus,
      slots = {},
      slotProps = {},
      TabIndicatorProps = {},
      TabScrollButtonProps = {},
      textColor = 'secondary',
      value,
      variant = 'standard',
      gap = 0,
      behavior,
      visibleScrollbar = false,
      classes,
      size: sizeProp = 'medium',
      ...other
    } = props
    const scrollable = behavior === 'scrollable'
    const vertical = orientation === 'vertical'

    const scrollStart = vertical ? 'scrollTop' : 'scrollLeft'
    const start = vertical ? 'top' : 'left'
    const end = vertical ? 'bottom' : 'right'
    const clientSize = vertical ? 'clientHeight' : 'clientWidth'
    const size = vertical ? 'height' : 'width'

    const ownerState = {
      ...props,
      Component,
      allowScrollButtonsMobile,
      indicatorColor,
      orientation,
      vertical,
      scrollButtons,
      textColor,
      variant,
      stacked: gap === 0,
      behavior,
      visibleScrollbar,
      fixed: !scrollable,
      hideScrollbar: scrollable && !visibleScrollbar,
      scrollableX: scrollable && !vertical,
      scrollableY: scrollable && vertical,
      centered: centered && !scrollable,
      scrollButtonsHideMobile: !allowScrollButtonsMobile,
    }

    const startScrollButtonIconProps = useSlotProps({
      elementType: slots.StartScrollButtonIcon,
      externalSlotProps: slotProps.startScrollButtonIcon,
      ownerState,
    })

    const endScrollButtonIconProps = useSlotProps({
      elementType: slots.EndScrollButtonIcon,
      externalSlotProps: slotProps.endScrollButtonIcon,
      ownerState,
    })

    if (process.env.NODE_ENV !== 'production') {
      if (centered && scrollable) {
        console.error(
          'You can not use the `centered={true}` and `behavior="scrollable"` properties ' +
            'at the same time on a `Tabs` component.'
        )
      }
    }

    const [mounted, setMounted] = React.useState(false)
    const [indicatorStyle, setIndicatorStyle] = React.useState(
      defaultIndicatorStyle
    )
    const [displayScroll, setDisplayScroll] = React.useState({
      start: false,
      end: false,
    })

    const [scrollerStyle, setScrollerStyle] = React.useState({
      overflow: 'hidden',
      scrollbarWidth: 0,
    })

    const valueToIndex = new Map()
    const tabsRef = React.useRef<HTMLDivElement>(null)
    const tabListRef = React.useRef<HTMLDivElement>(null)

    const getTabsMeta = () => {
      const tabsNode = tabsRef.current
      let tabsMeta
      if (tabsNode) {
        const rect = tabsNode.getBoundingClientRect()
        // create a new object with ClientRect class props + scrollLeft
        tabsMeta = {
          clientWidth: tabsNode.clientWidth,
          scrollLeft: tabsNode.scrollLeft,
          scrollTop: tabsNode.scrollTop,
          scrollLeftNormalized: getNormalizedScrollLeft(tabsNode, direction),
          scrollWidth: tabsNode.scrollWidth,
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
        }
      }

      let tabMeta
      if (tabsNode && value !== false) {
        const children = tabListRef.current!.children

        if (children.length > 0) {
          const tab = children[valueToIndex.get(value)]
          if (process.env.NODE_ENV !== 'production') {
            if (!tab) {
              console.error(
                [
                  `The \`value\` provided to the Tabs component is invalid.`,
                  `None of the Tabs' children match with "${value}".`,
                  valueToIndex.keys
                    ? `You can provide one of the following values: ${Array.from(
                        valueToIndex.keys()
                      ).join(', ')}.`
                    : null,
                ].join('\n')
              )
            }
          }
          tabMeta = tab ? tab.getBoundingClientRect() : null

          if (process.env.NODE_ENV !== 'production') {
            if (
              process.env.NODE_ENV !== 'test' &&
              !warnedOnceTabPresent &&
              tabMeta &&
              tabMeta.width === 0 &&
              tabMeta.height === 0 &&
              // if the whole Tabs component is hidden, don't warn
              tabsMeta?.clientWidth !== 0
            ) {
              tabsMeta = null
              console.error(
                [
                  'The `value` provided to the Tabs component is invalid.',
                  `The Tab with this \`value\` ("${value}") is not part of the document layout.`,
                  "Make sure the tab item is present in the document or that it's not `display: none`.",
                ].join('\n')
              )

              warnedOnceTabPresent = true
            }
          }
        }
      }
      return { tabsMeta, tabMeta }
    }

    const updateIndicatorState = useEventCallback(() => {
      const { tabsMeta, tabMeta } = getTabsMeta()
      let startValue = 0
      let startIndicator

      if (vertical) {
        startIndicator = 'top'
        if (tabMeta && tabsMeta) {
          startValue = tabMeta.top - tabsMeta.top + tabsMeta.scrollTop
        }
      } else {
        startIndicator = isRtl ? 'right' : 'left'
        if (tabMeta && tabsMeta) {
          const correction = isRtl
            ? tabsMeta.scrollLeftNormalized +
              tabsMeta.clientWidth -
              tabsMeta.scrollWidth
            : tabsMeta.scrollLeft
          startValue =
            (isRtl ? -1 : 1) *
            // @ts-ignore
            (tabMeta[startIndicator] - tabsMeta[startIndicator] + correction)
        }
      }

      const newIndicatorStyle = {
        [startIndicator]: startValue,
        // May be wrong until the font is loaded.
        [size]: tabMeta ? tabMeta[size] : 0,
      }

      // IE11 support, replace with Number.isNaN
      // eslint-disable-next-line no-restricted-globals
      if (
        // @ts-ignore
        isNaN(indicatorStyle[startIndicator]) ||
        // @ts-ignore
        isNaN(indicatorStyle[size])
      ) {
        setIndicatorStyle(newIndicatorStyle)
      } else {
        const dStart = Math.abs(
          // @ts-ignore
          indicatorStyle[startIndicator] - newIndicatorStyle[startIndicator]
        )
        // @ts-ignore
        const dSize = Math.abs(indicatorStyle[size] - newIndicatorStyle[size])

        if (dStart >= 1 || dSize >= 1) {
          setIndicatorStyle(newIndicatorStyle)
        }
      }
    })

    const scroll = (
      scrollValue: number,
      { animation = true }: { animation?: boolean } = {}
    ) => {
      if (animation) {
        animate(scrollStart, tabsRef.current!, scrollValue, {
          duration: transitionDurationStandard,
        })
      } else {
        tabsRef.current![scrollStart] = scrollValue
      }
    }

    const moveTabsScroll = (delta: number) => {
      let scrollValue = tabsRef.current![scrollStart]

      if (vertical) {
        scrollValue += delta
      } else {
        scrollValue += delta * (isRtl ? -1 : 1)
        // Fix for Edge
        scrollValue *= isRtl && detectScrollType() === 'reverse' ? -1 : 1
      }

      scroll(scrollValue)
    }

    const getScrollSize = () => {
      const containerSize = tabsRef.current![clientSize]
      let totalSize = 0
      const children = Array.from(tabListRef.current!.children)

      for (let i = 0; i < children.length; i += 1) {
        const tab = children[i]
        if (totalSize + tab![clientSize] > containerSize) {
          // If the first item is longer than the container size, then only scroll
          // by the container size.
          if (i === 0) {
            totalSize = containerSize
          }
          break
        }
        totalSize += tab![clientSize]
      }

      return totalSize
    }

    const handleStartScrollClick = () => {
      moveTabsScroll(-1 * getScrollSize())
    }

    const handleEndScrollClick = () => {
      moveTabsScroll(getScrollSize())
    }

    // TODO Remove <ScrollbarSize /> as browser support for hiding the scrollbar
    // with CSS improves.
    const handleScrollbarSizeChange = React.useCallback(
      (scrollbarWidth: number) => {
        setScrollerStyle({
          // @ts-ignore
          overflow: null,
          scrollbarWidth,
        })
      },
      []
    )

    const getConditionalElements = () => {
      const conditionalElements: {
        scrollbarSizeListener: JSX.Element | null
        scrollButtonStart: JSX.Element | null
        scrollButtonEnd: JSX.Element | null
      } = {
        scrollbarSizeListener: null,
        scrollButtonStart: null,
        scrollButtonEnd: null,
      }

      conditionalElements.scrollbarSizeListener = scrollable ? (
        <ScrollbarSize
          onChange={handleScrollbarSizeChange}
          className={clsx(classes?.scrollableX, classes?.hideScrollbar)}
        />
      ) : null

      const scrollButtonsActive = displayScroll.start || displayScroll.end
      const showScrollButtons =
        scrollable &&
        ((scrollButtons === 'auto' && scrollButtonsActive) ||
          scrollButtons === true)

      conditionalElements.scrollButtonStart = showScrollButtons ? (
        <ScrollButtonComponent
          slots={{ StartScrollButtonIcon: slots.StartScrollButtonIcon }}
          slotProps={{ startScrollButtonIcon: startScrollButtonIconProps }}
          orientation={orientation}
          direction={isRtl ? 'right' : 'left'}
          onClick={handleStartScrollClick}
          disabled={!displayScroll.start}
          {...TabScrollButtonProps}
          className={clsx(
            classes?.scrollButtons,
            TabScrollButtonProps.className,
            ownerState.scrollButtonsHideMobile && [
              style.scrollButtonsHideMobile,
              classes?.scrollButtonsHideMobile,
            ]
          )}
        />
      ) : null

      conditionalElements.scrollButtonEnd = showScrollButtons ? (
        <ScrollButtonComponent
          slots={{ EndScrollButtonIcon: slots.EndScrollButtonIcon }}
          slotProps={{
            endScrollButtonIcon: endScrollButtonIconProps,
          }}
          orientation={orientation}
          direction={isRtl ? 'left' : 'right'}
          onClick={handleEndScrollClick}
          disabled={!displayScroll.end}
          {...TabScrollButtonProps}
          className={clsx(
            classes?.scrollButtons,
            TabScrollButtonProps.className,
            ownerState.scrollButtonsHideMobile && [
              style.scrollButtonsHideMobile,
              classes?.scrollButtonsHideMobile,
            ]
          )}
        />
      ) : null

      return conditionalElements
    }

    const scrollSelectedIntoView = useEventCallback((animation?: boolean) => {
      const { tabsMeta, tabMeta } = getTabsMeta()

      if (!tabMeta || !tabsMeta) {
        return
      }

      if (tabMeta[start] < tabsMeta[start]) {
        // left side of button is out of view
        const nextScrollStart =
          tabsMeta[scrollStart] + (tabMeta[start] - tabsMeta[start])
        scroll(nextScrollStart, { animation })
      } else if (tabMeta[end] > tabsMeta[end]) {
        // right side of button is out of view
        const nextScrollStart =
          tabsMeta[scrollStart] + (tabMeta[end] - tabsMeta[end])
        scroll(nextScrollStart, { animation })
      }
    })

    const updateScrollButtonState = useEventCallback(() => {
      if (scrollable && scrollButtons !== false) {
        const {
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollWidth,
          clientWidth,
        } = tabsRef.current!
        let showStartScroll
        let showEndScroll

        if (vertical) {
          showStartScroll = scrollTop > 1
          showEndScroll = scrollTop < scrollHeight - clientHeight - 1
        } else {
          const scrollLeft = getNormalizedScrollLeft(
            tabsRef.current!,
            direction
          )
          // use 1 for the potential rounding error with browser zooms.
          showStartScroll = isRtl
            ? scrollLeft < scrollWidth - clientWidth - 1
            : scrollLeft > 1
          showEndScroll = !isRtl
            ? scrollLeft < scrollWidth - clientWidth - 1
            : scrollLeft > 1
        }

        if (
          showStartScroll !== displayScroll.start ||
          showEndScroll !== displayScroll.end
        ) {
          setDisplayScroll({ start: showStartScroll, end: showEndScroll })
        }
      }
    })

    React.useEffect(() => {
      const handleResize = debounce(() => {
        // If the Tabs component is replaced by Suspense with a fallback, the last
        // ResizeObserver's handler that runs because of the change in the layout is trying to
        // access a dom node that is no longer there (as the fallback component is being shown instead).
        // See https://github.com/mui/material-ui/issues/33276
        // TODO: Add tests that will ensure the component is not failing when
        // replaced by Suspense with a fallback, once React is updated to version 18
        if (tabsRef.current) {
          updateIndicatorState()
          updateScrollButtonState()
        }
      })
      const win = ownerWindow(tabsRef.current!)
      win.addEventListener('resize', handleResize)

      let resizeObserver: ResizeObserver

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(handleResize)
        Array.from(tabListRef.current!.children).forEach(child => {
          resizeObserver.observe(child)
        })
      }

      return () => {
        handleResize.clear()
        win.removeEventListener('resize', handleResize)
        if (resizeObserver) {
          resizeObserver.disconnect()
        }
      }
    }, [updateIndicatorState, updateScrollButtonState])

    const handleTabsScroll = React.useMemo(
      () =>
        debounce(() => {
          updateScrollButtonState()
        }),
      [updateScrollButtonState]
    )

    React.useEffect(() => {
      return () => {
        handleTabsScroll.clear()
      }
    }, [handleTabsScroll])

    React.useEffect(() => {
      setMounted(true)
    }, [])

    React.useEffect(() => {
      updateIndicatorState()
      updateScrollButtonState()
    })

    React.useEffect(() => {
      // Don't animate on the first render.
      scrollSelectedIntoView(defaultIndicatorStyle !== indicatorStyle)
    }, [scrollSelectedIntoView, indicatorStyle])

    React.useImperativeHandle(
      action,
      () => ({
        updateIndicator: updateIndicatorState,
        updateScrollButtons: updateScrollButtonState,
      }),
      [updateIndicatorState, updateScrollButtonState]
    )

    const indicator =
      variant === 'standard' ? (
        <span
          {...TabIndicatorProps}
          className={clsx(
            classes?.indicator,
            style.indicator,
            //@ts-ignore
            style[`indicator-${indicatorColor}`],
            //@ts-ignore
            isRtl && style[`indicator-rtl`],
            TabIndicatorProps.className
          )}
          style={{
            ...indicatorStyle,
            ...TabIndicatorProps.style,
          }}
        />
      ) : (
        ''
      )

    let childIndex = 0
    const children = React.Children.map(childrenProp, child => {
      if (!React.isValidElement(child)) {
        return null
      }

      if (process.env.NODE_ENV !== 'production') {
        if (isFragment(child)) {
          console.error(
            [
              "The Tabs component doesn't accept a Fragment as a child.",
              'Consider providing an array instead.',
            ].join('\n')
          )
        }
      }

      const childValue =
        child.props.value === undefined ? childIndex : child.props.value
      valueToIndex.set(childValue, childIndex)
      const selected = childValue === value

      childIndex += 1
      return React.cloneElement(child, {
        // @ts-ignore
        fullWidth: behavior === 'fullWidth',
        indicator: selected && !mounted && indicator,
        selected,
        selectionFollowsFocus,
        onChange,
        textColor,
        variant,
        stacked: ownerState.stacked,
        size: sizeProp,
        value: childValue,
        isVertical: orientation === 'vertical',
        isRtl: isRtl,
        ...(childIndex === 1 && value === false && !child.props.tabIndex
          ? { tabIndex: 0 }
          : {}),
      })
    })

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const list = tabListRef.current
      const currentFocus = ownerDocument(list).activeElement
      // Keyboard navigation assumes that [role="tab"] are siblings
      // though we might warn in the future about nested, interactive elements
      // as a a11y violation
      const role = currentFocus!.getAttribute('role')
      if (role !== 'tab') {
        return
      }

      let previousItemKey =
        orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'
      let nextItemKey =
        orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
      if (orientation === 'horizontal' && isRtl) {
        // swap previousItemKey with nextItemKey
        previousItemKey = 'ArrowRight'
        nextItemKey = 'ArrowLeft'
      }

      switch (event.key) {
        case previousItemKey:
          event.preventDefault()
          moveFocus(list!, currentFocus!, previousItem)
          break
        case nextItemKey:
          event.preventDefault()
          moveFocus(list!, currentFocus!, nextItem)
          break
        case 'Home':
          event.preventDefault()
          moveFocus(list!, null, nextItem)
          break
        case 'End':
          event.preventDefault()
          moveFocus(list!, null, previousItem)
          break
        default:
          break
      }
    }

    const conditionalElements = getConditionalElements()

    return (
      // tabs-root
      <Component
        className={clsx(
          style.tabsRoot,
          classes?.root,
          ownerState.vertical && [style.vertical, classes?.vertical],
          className
        )}
        ref={ref}
        {...other}
      >
        {conditionalElements.scrollButtonStart}
        {conditionalElements.scrollbarSizeListener}
        {/* tabs-scroller */}
        <div
          className={clsx(
            style.tabsScroller,
            classes?.scroller,
            ownerState.fixed && [style.fixed, classes?.fixed],
            ownerState.hideScrollbar && [
              style.hideScrollbar,
              classes?.hideScrollbar,
            ],
            ownerState.scrollableX && [style.scrollabelX, classes?.scrollableX],
            ownerState.scrollableY && [style.scrollabelY, classes?.scrollableY]
          )}
          style={{
            overflow: scrollerStyle.overflow,
            // TODO: devarsh - need to check below condition at MUI and resolve below condition.
            // [vertical ? `margin${isRtl ? 'Left' : 'Right'}` : 'marginBottom']:
            //   visibleScrollbar ? undefined : -scrollerStyle.scrollbarWidth,
          }}
          ref={tabsRef}
          onScroll={handleTabsScroll}
        >
          {/* The tablist isn't interactive but the tabs are */}
          {/* eslint-disable-next-line */} {/* @ts-ignore */}
          <div
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-orientation={orientation === 'vertical' ? 'vertical' : null}
            className={clsx(
              classes?.flexContainer,
              style.flexContainer,
              ownerState.vertical && [style.vertical, classes?.vertical],
              ownerState.centered && [style.centered, classes?.centered],
              //@ts-ignore
              isRtl && style[`flexContainer-rtl`]
            )}
            style={{ gap: gap }}
            onKeyDown={handleKeyDown}
            ref={tabListRef}
            role="tablist"
          >
            {children}
          </div>
          {mounted && indicator}
        </div>
        {conditionalElements.scrollButtonEnd}
      </Component>
    )
  }
)
